import { Component } from "react";

/**
 * Top-level error boundary.
 * Catches any uncaught render error in the tree and shows a recoverable
 * fallback instead of a blank white screen. Theme-aware via the app's
 * existing CSS variables (resolved by <html data-theme>).
 *
 * Kept as a standalone class component (error boundaries must be classes)
 * so App.jsx internals are untouched.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the console for debugging; swap for a real reporter later.
    console.error("[ADVANCD] Uncaught render error:", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "An unexpected error occurred.";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "var(--bg, #0d0d0d)",
          color: "var(--text, #e0e0e0)",
          fontFamily:
            '"Helvetica Neue", Helvetica, Arial, system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 440,
            background: "var(--panel, #0a0a0a)",
            border: "1px solid var(--panel-border, #222)",
            borderRadius: 16,
            padding: "32px 28px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-display, "Bebas Neue", sans-serif)',
              fontSize: 28,
              letterSpacing: 1,
              color: "var(--accent, #f0a500)",
              marginBottom: 8,
            }}
          >
            ADVANCD
          </div>
          <h1 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: "var(--text-mut, #7d7d7d)",
              margin: "0 0 20px",
            }}
          >
            The app hit an unexpected error and couldn't finish loading this view.
            Reloading usually fixes it. If it keeps happening, let us know.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              appearance: "none",
              border: "none",
              cursor: "pointer",
              background: "var(--accent, #f0a500)",
              color: "var(--accent-fg, #0d0d0d)",
              fontSize: 14,
              fontWeight: 700,
              padding: "10px 22px",
              borderRadius: 8,
            }}
          >
            Reload
          </button>
          <details style={{ marginTop: 18, textAlign: "left" }}>
            <summary
              style={{
                fontSize: 12,
                color: "var(--text-faint, #555)",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              Technical details
            </summary>
            <pre
              style={{
                marginTop: 8,
                fontSize: 11,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "var(--text-mut, #7d7d7d)",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              }}
            >
              {msg}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
